import * as inflection from 'inflection'

import apps from './locales/vmisc/apps.json'
import auth from './locales/vmisc/auth.json'
import settings from './locales/vmisc/settings.json'
import environments from './locales/vmisc/environments.json'
import changes from './locales/vmisc/changes.json'
import executions from './locales/vmisc/executions.json'
import onboarding from './locales/vmisc/onboarding.json'
import portal from './locales/vmisc/portal.json'
import organizations from './locales/vmisc/organizations.json'
import flowNotifications from './locales/vmisc/flow_notifications.json'
import getStarted from './locales/vmisc/get_started.json'
import governance from './locales/vmisc/governance.json'

import helpers from './locales/util/helpers.json'

import brief from './locales/brief.json'
import chat from './locales/chat.json'
import collections from './locales/collections.json'

import general from './locales/general.json'
import presentation from './locales/presentation.json'
import schema from './locales/schema.json'
import start from './locales/start.json'
import studio from './locales/studio.json'
import templates from './locales/templates.json'

import basicsForm from './locales/apps/basics_forms.json'
import data from './locales/apps/data.json'
import documents from './locales/apps/documents.json'
import processSubflows from './locales/apps/process_subflows.json'
import textAi from './locales/apps/text_ai.json'

import { getUserLanguage } from './l.lib'

const localesMapping = {
  apps,
  auth,
  settings,
  environments,
  changes,
  executions,
  onboarding,
  portal,
  organizations,
  flowNotifications,
  getStarted,
  governance,
  helpers,
  brief,
  chat,
  start,
  studio,
  general,
  collections,
  presentation,
  schema,
  basicsForm,
  data,
  documents,
  processSubflows,
  textAi,
  templates
}

const LOCALE_PATTERN = /.*%[A-Za-z0-9]{5}/
const INNER_LOCALE_PATTERN = /%%([a-z]{2}:(?:(?!%%|;;).)+)(;;[a-z]{2}:(?:(?!%%|;;).)+)*%%/i
const ID_PATTERN = /%[A-Za-z0-9]{5}/
const LANG_PATTERN = /^[a-z]{2,5}(-[a-z0-9]{2,8})?$/i


const formatContent = (text, placeholderParams, formatParam) =>
  applyFormat(applyPlaceholders(text, placeholderParams), formatParam)

const applyPlaceholders = (text, params) =>
  (text ?? "").replace(/\{ *(\w+) *\}/g, (_, key) =>
    key in params ? params[key] : `{${key}}`)

const applyFormat = (value, fmt) => {
  if (!fmt) return value

  if (fmt.toLowerCase() === 'aa') return value.toLowerCase()
  if (fmt === 'AA') return value.toUpperCase()

  let hasArray = false
  if (value.endsWith('[]')) {
    hasArray = true
    value = value.slice(0, -2)
  }

  let suffixChar = ''
  if (/^[/.]/.test(fmt)) {
    suffixChar = fmt[0]
    fmt = fmt.slice(1)
    if (value) value = suffixChar + value
  }

  if (fmt.includes('_')) {
    value = inflection.underscore(value)
  } else if (fmt.includes(' ')) {
    value = inflection.underscore(value).replace(/_/g, ' ')
  } else {
    value = inflection.camelize(value, fmt.startsWith('A'))
    if (!fmt.startsWith('A')) {
      value = value[0].toLowerCase() + value.slice(1)
    }
  }

  if (/[/.]$/.test(fmt)) {
    suffixChar = fmt.slice(-1)
    fmt = fmt.slice(0, -1)
    if (value) value += suffixChar
  }

  if (/s$/.test(fmt)) {
    const lang = getUserLanguage()
    value =
      lang === 'es'
        ? value.endsWith('s')
          ? value
          : value + 's'
        : inflection.pluralize(value)
  } else if (/u$/.test(fmt)) {
    value = inflection.singularize(value)
  }

  if (fmt.startsWith('A_A') || fmt.startsWith('A A')) {
    value = value.toUpperCase()
  } else if (fmt.startsWith('A')) {
    value = value[0].toUpperCase() + value.slice(1)
  } else if (fmt.startsWith('a')) {
    value = value[0].toLowerCase() + value.slice(1)
  }

  return hasArray ? value + '[]' : value
}

// Message contents often arrive wrapped in markup (e.g. "<p>Texto%AB123</p>"),
// so tags are ignored when comparing a content against its locale key.
const stripTags = (s) => (s ?? '').replace(/<[^>]*>/g, '').trim()

const cleanId = (s) =>
  (s ?? '')
    .toString()
    .trim()
    .toUpperCase()
    .padStart(5, '0')

// Every %ID with the first locale key that declares it, in localesMapping order. Built once: scanning
// all ~4000 keys on each call cost ~0.4 ms, and a screen renders dozens of strings per update.
const buildLocaleEntriesById = () => {
  const entries = new Map()
  for (const localeMapping of Object.values(localesMapping)) {
    for (const localeKey in localeMapping) {
      const localeId = cleanId(localeKey.match(ID_PATTERN)[0].slice(1))
      if (!entries.has(localeId)) entries.set(localeId, { localeKey, localeMapping })
    }
  }
  return entries
}

const LOCALE_ENTRIES_BY_ID = buildLocaleEntriesById()


function parseInnerLocales(match) {
  const stripped = match.replace(/%%/g, '')
  const chunks = stripped.split(';;')

  const map = Object.create(null)
  let firstText = null

  for (const chunkRaw of chunks) {
    const chunk = (chunkRaw ?? '').toString()
    if (!chunk) continue

    const i = chunk.indexOf(':')
    if (i <= 0) continue

    const lang = chunk.slice(0, i).trim().toLowerCase()
    if (!LANG_PATTERN.test(lang)) continue

    const text = chunk.slice(i + 1)
    if (firstText === null) firstText = text
    if (!(lang in map)) map[lang] = text
  }

  if (firstText === null) return { ok: false, stripped }
  return { ok: true, stripped, map, firstText }
}

/**
 * Translates a string with key "DefaultText%ABCDE" or inner locales "%%en:Hello;;es:Hola%%" into the user's language.,
 * validates that the ID has 5 alphanumeric characters,
 * handles placeholders and formatting.
 *
 * @param {string} content — "Default text%ABCDE" or "%%en:Hello;;es:Hola%%"
 * @param {object} params  — { placeholder: 'value', ... }
 * @param {string|null} format — optional format ('aA', 'As', etc.)
 */
export default function l(content, params = {}, format = null) {

  const isDeveloper = sessionStorage.getItem("is_developer") == "true"
  const showDebugLocales = sessionStorage.getItem("show_debug_locales") == "true"
  const shouldThrowLocaleError = isDeveloper && showDebugLocales
  const lang = getUserLanguage()

  const reportLocaleIssue = (message) => {
    if (shouldThrowLocaleError) throw new Error(message)
    console.warn(`[l] ${message}`)
  }

  // Inner locales parsing: %%en:Hello;;es:Hola%%
  if (typeof content === 'string' && INNER_LOCALE_PATTERN.test(content)) {
    const match = content.match(INNER_LOCALE_PATTERN)[0]
    const parsed = parseInnerLocales(match)
    if (parsed?.ok) {
      const langFull = (lang ?? 'es').toLowerCase()
      const baseLang = langFull.split(/[-_.]/)[0]
      let txt = (parsed.map[langFull] ?? parsed.map[baseLang] ?? parsed.firstText ?? '').toString()
      let val = formatContent(txt, params, format)
      return content.replace(match, val)
    }
  }

  // If content is not a string or doesn't match the locale pattern, just apply placeholders and formatting
  if (typeof content !== 'string' || !LOCALE_PATTERN.test(content))
    return formatContent((content ?? '').toString(), params, format)

  // Normal parsing with DefaultText%ID
  const id = cleanId(content.match(ID_PATTERN)[0].slice(1))
  const defaultText = content.replace(`%${id}`, '').trim()

  const localeEntry = LOCALE_ENTRIES_BY_ID.get(id)
  if (localeEntry) {
    const { localeKey, localeMapping } = localeEntry
    const localeDefaultText = localeKey.replace(`%${id}`, '').trim()
    if (stripTags(defaultText) !== stripTags(localeDefaultText)) {
      reportLocaleIssue(`Key “%${id}“ exists but with different format: Content: “${content}”  Locale: “${localeKey}”.`)
    }

    const localeTranslation = lang == 'es' ? localeDefaultText : localeMapping[localeKey][lang]
    if (localeTranslation) {
      return formatContent(isDeveloper && showDebugLocales ? localeKey : localeTranslation, params, format)
    }

    reportLocaleIssue(`Key “${content}“ exists but no translation for language: “${lang}”.`)
    return formatContent(defaultText, params, format)
  }

  reportLocaleIssue(`Locale key “${content}” not found in any locale file. Showing text without %ID.`)
  return formatContent(defaultText, params, format)
}
